import React, { useState, useEffect, useCallback } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { TrendingUp, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { toast } from '../../lib/safeToast';

export default function BackOfficeAnalyticsMerchant() {
  const { api } = useAuth();
  const { isLive } = useEnvironment();
  const [monthly, setMonthly] = useState([]);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [monthlyRes, dailyRes] = await Promise.all([
        api.get('/backoffice/analytics/merchant-growth'),
        api.get('/backoffice/new-registrations-7d'),
      ]);
      setMonthly(monthlyRes.data?.data || []);
      setDaily(dailyRes.data?.data || []);
    } catch { toast.error('Failed to load analytics'); } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData, isLive]);

  return (
    <BackOfficeLayout>
      <div className="space-y-8">
        <div className="flex items-center justify-between pb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2"><TrendingUp className="w-7 h-7 text-[#0B6445]" />Merchant Growth</h1>
            <p className="text-[#0E1B16]/70 mt-2">Merchant registration trends and growth analytics.</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
        ) : (
          <>
            <Card className="border-[#DCE4E0] shadow-sm">
              <CardHeader className="pb-3 pt-6"><CardTitle className="text-base text-[#073B2E]">Monthly Registrations (Last 6 Months)</CardTitle></CardHeader>
              <CardContent className="pb-6">
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#0B6445" radius={[4, 4, 0, 0]} name="New Merchants" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#DCE4E0] shadow-sm mt-10">
              <CardHeader className="pb-3 pt-6"><CardTitle className="text-base text-[#073B2E]">Daily Registrations (Last 7 Days)</CardTitle></CardHeader>
              <CardContent className="pb-6">
                <div className="h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={daily}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#0B6445" strokeWidth={2} dot={{ r: 4 }} name="Registrations" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </BackOfficeLayout>
  );
}
